/**
 * Pure view model for the agent panel transcript (FEAT-1698).
 *
 * The transcript is an ordered list of discriminated items rather than a role/content pair list,
 * because a single turn interleaves streamed assistant text with tool-activity rows. Keeping the
 * transitions pure makes the streaming state machine unit-testable without React or `fetch`.
 */
import { AGENT_STREAM_EVENT, AGENT_THREAD_MESSAGE_KIND, parseJsonObject, readStringField } from './protocol';
import type { ParsedJsonObject } from './protocol';
import type { AgentStreamEvent, AgentThreadMessage } from './protocol';

/** Discriminants for the renderable rows of an agent transcript. */
export const AGENT_CHAT_ITEM = {
  USER_MESSAGE: 'USER_MESSAGE',
  ASSISTANT_MESSAGE: 'ASSISTANT_MESSAGE',
  TOOL_ACTIVITY: 'TOOL_ACTIVITY',
  TURN_ERROR: 'TURN_ERROR',
} as const;

/** Lifecycle of one agent tool call as reflected in the transcript. */
export const AGENT_TOOL_STATUS = {
  RUNNING: 'RUNNING',
  SUCCEEDED: 'SUCCEEDED',
  FAILED: 'FAILED',
} as const;

/** Union of tool-activity statuses. */
export type AgentToolStatus = (typeof AGENT_TOOL_STATUS)[keyof typeof AGENT_TOOL_STATUS];

/** One row of the agent transcript. */
export type AgentChatItem =
  | { type: typeof AGENT_CHAT_ITEM.USER_MESSAGE; id: string; text: string }
  | {
      type: typeof AGENT_CHAT_ITEM.ASSISTANT_MESSAGE;
      id: string;
      text: string;
      isStreaming: boolean;
    }
  | {
      type: typeof AGENT_CHAT_ITEM.TOOL_ACTIVITY;
      id: string;
      toolCallId: string;
      toolName: string;
      status: AgentToolStatus;
    }
  | {
      type: typeof AGENT_CHAT_ITEM.TURN_ERROR;
      id: string;
      /** Stable error code; the panel localizes it via `agentErrorMessageKey`. */
      code: string;
      /**
       * Message that is already localized by the backend that produced it (GraphQL mint failures).
       * `null` for platform-api transport errors, whose English operator text must not be rendered.
       */
      localizedMessage: string | null;
    };

/** Supplies unique transcript item ids; injected so tests stay deterministic. */
export type ChatItemIdFactory = (prefix: string) => string;

/** Default id factory: monotonic per module load, stable enough for React keys. */
export const createChatItemIdFactory = (): ChatItemIdFactory => {
  let counter = 0;
  return (prefix: string) => {
    counter += 1;
    return `${prefix}-${counter}`;
  };
};

const sealStreamingText = (items: readonly AgentChatItem[]): AgentChatItem[] =>
  items.map((item) =>
    item.type === AGENT_CHAT_ITEM.ASSISTANT_MESSAGE && item.isStreaming ? { ...item, isStreaming: false } : item
  );

/** Appends the user's outgoing message to the transcript. */
export function appendUserMessage(
  items: readonly AgentChatItem[],
  text: string,
  nextId: ChatItemIdFactory
): AgentChatItem[] {
  return [...items, { type: AGENT_CHAT_ITEM.USER_MESSAGE, id: nextId('USER'), text }];
}

/**
 * Applies one parsed stream event to the transcript.
 *
 * Deltas extend the trailing streaming assistant bubble; a tool call seals it so post-tool text
 * renders as a new bubble below the activity row, which matches how the backend orders its events.
 */
export function applyStreamEvent(
  items: readonly AgentChatItem[],
  event: AgentStreamEvent,
  nextId: ChatItemIdFactory
): AgentChatItem[] {
  switch (event.type) {
    case AGENT_STREAM_EVENT.MESSAGE_DELTA: {
      const last = items[items.length - 1];
      if (last && last.type === AGENT_CHAT_ITEM.ASSISTANT_MESSAGE && last.isStreaming) {
        return [...items.slice(0, -1), { ...last, text: `${last.text}${event.delta}` }];
      }
      return [
        ...items,
        {
          type: AGENT_CHAT_ITEM.ASSISTANT_MESSAGE,
          id: nextId('ASSISTANT'),
          text: event.delta,
          isStreaming: true,
        },
      ];
    }
    case AGENT_STREAM_EVENT.TOOL_CALL_STARTED:
      return [
        ...sealStreamingText(items),
        {
          type: AGENT_CHAT_ITEM.TOOL_ACTIVITY,
          id: nextId('TOOL'),
          toolCallId: event.toolCallId,
          toolName: event.toolName,
          status: AGENT_TOOL_STATUS.RUNNING,
        },
      ];
    case AGENT_STREAM_EVENT.TOOL_CALL_RESULT: {
      const status = event.isError ? AGENT_TOOL_STATUS.FAILED : AGENT_TOOL_STATUS.SUCCEEDED;
      const matched = items.some(
        (item) => item.type === AGENT_CHAT_ITEM.TOOL_ACTIVITY && item.toolCallId === event.toolCallId
      );
      if (!matched) {
        return [
          ...items,
          {
            type: AGENT_CHAT_ITEM.TOOL_ACTIVITY,
            id: nextId('TOOL'),
            toolCallId: event.toolCallId,
            toolName: event.toolName,
            status,
          },
        ];
      }
      return items.map((item) =>
        item.type === AGENT_CHAT_ITEM.TOOL_ACTIVITY && item.toolCallId === event.toolCallId ? { ...item, status } : item
      );
    }
    case AGENT_STREAM_EVENT.TURN_COMPLETE:
      return finalizeTranscript(items);
    case AGENT_STREAM_EVENT.ERROR:
      return [
        ...finalizeTranscript(items),
        {
          type: AGENT_CHAT_ITEM.TURN_ERROR,
          id: nextId('ERROR'),
          code: event.code,
          // The SSE `error` payload message is untranslated operator text; localize by code instead.
          localizedMessage: null,
        },
      ];
    default:
      return [...items];
  }
}

/**
 * Closes out a turn: no bubble stays in the streaming state and no activity row keeps spinning.
 *
 * A tool row still `RUNNING` when the turn ends never reported a result (aborted, timed out, or lock
 * lost), so it settles as failed rather than leaving an indefinite spinner in the transcript.
 */
export function finalizeTranscript(items: readonly AgentChatItem[]): AgentChatItem[] {
  return sealStreamingText(items).map((item) =>
    item.type === AGENT_CHAT_ITEM.TOOL_ACTIVITY && item.status === AGENT_TOOL_STATUS.RUNNING
      ? { ...item, status: AGENT_TOOL_STATUS.FAILED }
      : item
  );
}

/**
 * Reads assistant text from a persisted row.
 *
 * Canonical storage keeps either a single `text` string or an array of `{ type: 'text', text }`
 * blocks (see `buildPersistedTurnMessages`), so both shapes are accepted.
 */
const assistantTextFromContent = (content: ParsedJsonObject): string => {
  const text = readStringField(content, 'text');
  if (text.length > 0) {
    return text;
  }
  if (!Array.isArray(content.blocks)) {
    return '';
  }
  return content.blocks
    .map((block) => {
      const parsedBlock = parseJsonObject(block);
      return parsedBlock ? readStringField(parsedBlock, 'text') : '';
    })
    .filter((blockText) => blockText.length > 0)
    .join('\n\n');
};

/** Rebuilds a transcript from persisted thread history for resume/history views. */
export function toChatItemsFromHistory(messages: readonly AgentThreadMessage[]): AgentChatItem[] {
  const items: AgentChatItem[] = [];

  messages.forEach((message, index) => {
    const id = message.id.length > 0 ? message.id : `HISTORY-${index}`;
    switch (message.kind) {
      case AGENT_THREAD_MESSAGE_KIND.USER: {
        const text = readStringField(message.content, 'text');
        if (text.length > 0) {
          items.push({ type: AGENT_CHAT_ITEM.USER_MESSAGE, id, text });
        }
        break;
      }
      case AGENT_THREAD_MESSAGE_KIND.ASSISTANT: {
        const text = assistantTextFromContent(message.content);
        if (text.length > 0) {
          items.push({
            type: AGENT_CHAT_ITEM.ASSISTANT_MESSAGE,
            id,
            text,
            isStreaming: false,
          });
        }
        break;
      }
      case AGENT_THREAD_MESSAGE_KIND.TOOL_CALL:
        items.push({
          type: AGENT_CHAT_ITEM.TOOL_ACTIVITY,
          id,
          toolCallId: readStringField(message.content, 'id'),
          toolName: readStringField(message.content, 'name'),
          status: AGENT_TOOL_STATUS.RUNNING,
        });
        break;
      case AGENT_THREAD_MESSAGE_KIND.TOOL_RESULT: {
        const toolCallId = readStringField(message.content, 'tool_use_id');
        const status = message.content.is_error === true ? AGENT_TOOL_STATUS.FAILED : AGENT_TOOL_STATUS.SUCCEEDED;
        const target = items.findIndex(
          (item) => item.type === AGENT_CHAT_ITEM.TOOL_ACTIVITY && item.toolCallId === toolCallId
        );
        const existing = target === -1 ? undefined : items[target];
        if (existing && existing.type === AGENT_CHAT_ITEM.TOOL_ACTIVITY) {
          items[target] = { ...existing, status };
        }
        break;
      }
      default:
        break;
    }
  });

  return finalizeTranscript(items);
}
